/**
 * Azure Speech Translation SDK 共享工具（V2 useSpeechTranslation + CR V3 H5 bridge 复用）
 */

/** 从 SDK translations 用 azureCode 取单条译文（兼容 privMap 包装） */
export function getAzureTranslation(
  translations: Map<string, string> | Record<string, unknown> | null | undefined,
  code: string,
): string | undefined {
  if (!translations || !code) {
    return undefined
  }
  if (translations instanceof Map) {
    return translations.get(code)
  }
  const o = translations as Record<string, unknown>
  const map = o.privMap instanceof Map ? (o.privMap as Map<string, string>) : null
  if (map) {
    return map.get(code)
  }
  if (typeof (o as { get?: (k: string) => unknown }).get === 'function') {
    const v = (o as { get: (k: string) => unknown }).get(code)
    return typeof v === 'string' ? v : undefined
  }
  return undefined
}

/** 无接口简写时推导 Azure 目标码 */
export function fallbackAzureTargetCode(locale: string): string {
  const lower = locale.toLowerCase()
  if (lower.startsWith('zh-cn') || lower === 'zh-hans') {
    return 'zh-Hans'
  }
  if (lower.startsWith('zh-tw') || lower.startsWith('zh-hk') || lower === 'zh-hant') {
    return 'zh-Hant'
  }
  return locale.split('-')[0] || locale
}

export interface BuildTranslationsOptions {
  /** 输出 Record 的 key 映射；默认与 targetLangs 相同 */
  mapOutputKey?: (targetLang: string, index: number) => string
}

/** 按 targetLangs ↔ azureCodes 从 SDK 结果构建译文 Record */
export function buildTranslationsFromAzureCodes(
  translations: Map<string, string> | Record<string, unknown> | null | undefined,
  targetLangs: string[],
  azureCodes: string[],
  options?: BuildTranslationsOptions,
): Record<string, string> {
  const trans: Record<string, string> = {}
  if (targetLangs.length !== azureCodes.length) {
    return trans
  }
  const mapKey = options?.mapOutputKey ?? ((lang: string) => lang)
  for (let i = 0; i < targetLangs.length; i++) {
    const code = (azureCodes[i] ?? '').trim()
    const text = code ? getAzureTranslation(translations, code) : undefined
    if (text !== undefined && text !== '') {
      trans[mapKey(targetLangs[i], i)] = text
    }
  }
  return trans
}

/** 与 useSpeechTranslation 一致的低延迟断句参数（D20） */
export function applySpeechTranslationSegmentation(
  config: { setProperty: (id: unknown, value: string) => void },
  speechsdk: { PropertyId: Record<string, unknown> },
  silenceTimeoutMs?: number,
): void {
  const pid = speechsdk.PropertyId
  config.setProperty(pid.Speech_SegmentationStrategy, 'Time')
  config.setProperty(pid.Speech_SegmentationMaximumTimeMs, '30000')
  config.setProperty(
    pid.Speech_SegmentationSilenceTimeoutMs,
    String(silenceTimeoutMs ?? 500),
  )
  config.setProperty(pid.Speech_StartEventSensitivity, 'high')
}

export function resolveEffectiveAzureTargetCodes(
  targetLanguages: string[],
  targetLanguageAzureCodes?: string[],
): string[] {
  const azureCodes =
    targetLanguageAzureCodes?.length === targetLanguages.length
      ? targetLanguageAzureCodes
      : targetLanguages.map(fallbackAzureTargetCode)
  return azureCodes.map((c, i) =>
    (c ?? '').trim() || fallbackAzureTargetCode(targetLanguages[i] ?? ''),
  )
}

/** 向 TranslationRecognizer 注入 PhraseList（D19 glossary） */
export function applyPhraseListToRecognizer(
  recognizer: { close?: () => void },
  phrases: string[],
  speechsdk: { PhraseListGrammar: { fromRecognizer: (r: unknown) => { addPhrase: (p: string) => void } } },
): void {
  const trimmed = phrases.map((p) => p.trim()).filter(Boolean)
  if (!trimmed.length) {
    return
  }
  const grammar = speechsdk.PhraseListGrammar.fromRecognizer(recognizer)
  for (const phrase of trimmed) {
    grammar.addPhrase(phrase)
  }
}
